import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@urc-ui/store/types';
import { FREQUENCY, ONCE_SCHEDULE_DATE_OPTION } from '@flexible-reports/utils/schedule-constants';
import { REPORT_FORMAT } from '@flexible-reports/utils/report-format-constants';
import { getDefaultReportName } from '@flexible-reports/store/slices/report-form/report-form-slice-utils';
import {
  ColumnSettingOptions,
  FlexibleReportPeriod,
  FlexibleSubscription,
  FormModificationState,
  ReportDelivery,
  ReportSchedule,
  UpdateSelectedFamiliesProps
} from '@flexible-reports/store/slices/types';
import { SelectedFamily } from '@flexible-reports/store/metadata.types';
import { DEFAULT_CHARACTER_LIMIT } from '@flexible-reports/components/report-name-card/report-name-input';
import { getDefaultReportScheduleForFrequency, isDateInPast } from '@flexible-reports/utils/schedule-utils';
import type { FieldValidationError } from '@flexible-reports/store/slices/report-form/validation/types';
import { ServerValidationError } from '@flexible-reports/store/api/server-error-codes';
import {
  buildClassifications,
  filterSelectedDimensionFamilies,
  filterSelectedMetricFamilies,
  updateSelectionsByClassifications
} from '@flexible-reports/utils/classification-utils';
import {
  buildFlatSelectedFamilies,
  buildSelectedFamiliesByMap,
  columnAttributeToFamilyIdMap,
  flattenSelectedFamilies
} from '@flexible-reports/pages/shared/column-picker-editor/column-picker-editor-utils';

export type ReportFilters = {
  managerAccountIds: string[];
  advertiserAccountIds: string[];
  countries: string[];
  campaignIds: string[];
  useAllCampaigns: boolean;
  productTypes: string[];
};

export type FlexibleReportFormState = Pick<
  FlexibleSubscription,
  'reportName' | 'reportFilters' | 'reportPeriod' | 'reportDelivery' | 'reportFormat' | 'reportSchedule' | 'reportFields'
> & {
  // allowing `null` for new report creation
  reportConfigurationId: string | null;
  templateId?: string;
  selectedDimensions: SelectedFamily[];
  selectedMetrics: SelectedFamily[];
  columnSettings: ColumnSettingOptions;
  formModificationState: FormModificationState;
  // New validation state
  fieldValidationErrors: FieldValidationError[];
  submitErrors: ServerValidationError[];
  // this is only set on settings page load to track if the original subscription had an MA and we need to clear it out
  linkedManagerAccountIds: string[];
};

const initialFormModificationState: FormModificationState = {
  isReportNameModified: false,
  areFiltersModified: false,
  areColumnsModified: false,
  isReportPeriodModified: false,
  isDeliveryModified: false,
  isScheduleModified: false,
  isCurrencyOfViewModified: false
};

const initialState: FlexibleReportFormState = {
  reportConfigurationId: null,
  reportName: getDefaultReportName(),
  reportFilters: {
    managerAccountIds: [],
    advertiserAccountIds: [],
    countries: [],
    campaignIds: [],
    useAllCampaigns: true,
    productTypes: []
  },
  selectedDimensions: [],
  selectedMetrics: [],
  columnSettings: {
    selectedCurrencyOfView: null
  },
  reportPeriod: {
    preset: 'LAST_7_DAYS',
    reportingTimezone: 'CAMPAIGN_TIMEZONE'
  },
  reportFields: [],
  reportFormat: REPORT_FORMAT.CSV,
  reportSchedule: {
    frequency: FREQUENCY.ONCE,
    onceDateOption: ONCE_SCHEDULE_DATE_OPTION.TODAY
  },
  reportDelivery: {
    emails: []
  },
  formModificationState: initialFormModificationState,
  fieldValidationErrors: [],
  submitErrors: [],
  linkedManagerAccountIds: []
};

const FlexibleReportFormSlice = createSlice({
  name: 'flexibleReportsCreatePageSlice',
  initialState,
  reducers: {
    setFormSettingsFromSubscription: (state, action: PayloadAction<FlexibleSubscription>) => {
      const report = action.payload;
      state.reportConfigurationId = report.reportConfigurationId;
      state.formModificationState = initialFormModificationState;
      state.reportSchedule = report.reportSchedule;
      state.reportDelivery = report.reportDelivery;
      state.reportName = report.reportName;
      state.reportFormat = report.reportFormat;
      state.reportFilters = report.reportFilters;
      state.reportPeriod = report.reportPeriod;
      state.fieldValidationErrors = [];
      state.columnSettings = report.columnSettings;
      state.linkedManagerAccountIds = (report.linkedAccounts ?? [])
        .filter(account => account.managerAccountId)
        .map(account => account.managerAccountId) as string[];
    },
    setFilters: (state, action: PayloadAction<ReportFilters>) => {
      state.reportFilters = action.payload;
      state.formModificationState.areFiltersModified = true;
    },
    setManagerAccountsFilter: (state, action: PayloadAction<ReportFilters['managerAccountIds']>) => {
      state.reportFilters.managerAccountIds = action.payload;
      state.formModificationState.areFiltersModified = true;
    },
    setAdvertisersFilter: (state, action: PayloadAction<ReportFilters['advertiserAccountIds']>) => {
      state.reportFilters.advertiserAccountIds = action.payload;
      state.formModificationState.areFiltersModified = true;
    },
    setCampaignCountryFilter: (state, action: PayloadAction<ReportFilters['countries']>) => {
      state.reportFilters.countries = action.payload;
      state.formModificationState.areFiltersModified = true;
    },
    setCampaignIdsFilter: (state, action: PayloadAction<ReportFilters['campaignIds']>) => {
      state.reportFilters.campaignIds = action.payload;
      state.formModificationState.areFiltersModified = true;
    },
    setUseAllCampaigns: (state, action: PayloadAction<boolean>) => {
      state.reportFilters.useAllCampaigns = action.payload;
      if (action.payload) {
        state.reportFilters.campaignIds = [];
      }
      state.formModificationState.areFiltersModified = true;
    },
    setProductTypeFilter: (state, action: PayloadAction<ReportFilters['productTypes']>) => {
      state.reportFilters.productTypes = action.payload;
      state.formModificationState.areFiltersModified = true;
    },
    setReportPeriod: (state, action: PayloadAction<FlexibleReportPeriod>) => {
      state.reportPeriod = action.payload;
      state.formModificationState.isReportPeriodModified = true;
    },
    setTemplateId: (state, action: PayloadAction<{ templateId: string }>) => {
      const { templateId } = action.payload;
      state.templateId = templateId;
    },
    setReportName: (state, action: PayloadAction<{ updatedReportName: string }>) => {
      const { updatedReportName } = action.payload;
      state.reportName = updatedReportName;
    },
    setReportSchedule: (state, action: PayloadAction<{ reportSchedule: ReportSchedule }>) => {
      const { reportSchedule } = action.payload;
      state.reportSchedule = reportSchedule;
    },
    setReportDelivery: (state, action: PayloadAction<{ reportDelivery: ReportDelivery }>) => {
      const { reportDelivery } = action.payload;
      state.reportDelivery = reportDelivery;
    },
    setSelectedDimensions: (state, action: PayloadAction<{ selectedDimensions: SelectedFamily[] }>) => {
      const { selectedDimensions } = action.payload;
      state.selectedDimensions = selectedDimensions;
    },
    removeDimensionFamily: (state, action: PayloadAction<{ removedDimensionFamilyId: string }>) => {
      const { removedDimensionFamilyId } = action.payload;
      const familyIndex = state.selectedDimensions.findIndex(dimension => dimension.id === removedDimensionFamilyId);
      if (familyIndex !== -1) {
        state.selectedDimensions.splice(familyIndex, 1);
        state.formModificationState.areColumnsModified = true;
      }
    },
    removeAllDimensions: state => {
      state.selectedDimensions = [];
      state.formModificationState.areColumnsModified = true;
    },
    setSelectedMetrics: (state, action: PayloadAction<{ selectedMetrics: SelectedFamily[] }>) => {
      const { selectedMetrics } = action.payload;
      state.selectedMetrics = selectedMetrics;
    },
    removeMetricFamily: (state, action: PayloadAction<{ removedMetricFamilyId: string }>) => {
      const { removedMetricFamilyId } = action.payload;
      const familyIndex = state.selectedMetrics.findIndex(metric => metric.id === removedMetricFamilyId);
      if (familyIndex !== -1) {
        state.selectedMetrics.splice(familyIndex, 1);
        state.formModificationState.areColumnsModified = true;
      }
    },
    removeAllMetrics: state => {
      state.selectedMetrics = [];
      state.formModificationState.areColumnsModified = true;
    },
    updateReportName: (state, action: PayloadAction<{ updatedReportName: string }>) => {
      const { updatedReportName } = action.payload;
      state.reportName = updatedReportName;
      state.formModificationState.isReportNameModified = true;
    },
    updateReportSchedule: (state, action: PayloadAction<{ reportSchedule: ReportSchedule }>) => {
      const { reportSchedule } = action.payload;
      state.reportSchedule = reportSchedule;
      state.formModificationState.isScheduleModified = true;
    },
    updateReportDelivery: (state, action: PayloadAction<{ reportDelivery: ReportDelivery }>) => {
      const { reportDelivery } = action.payload;
      state.reportDelivery = reportDelivery;
      state.formModificationState.isDeliveryModified = true;
    },
    updateSelectedFamilies: (state, action: PayloadAction<UpdateSelectedFamiliesProps>) => {
      const { selectedMetrics, selectedDimensions, metadata, currentColumnSettings } = action.payload;
      const { complimentaryFieldsMap = {}, requiredFields = [], dimFamilies = [] } = metadata ?? {};

      const isNewCurrencySelection = !state.columnSettings.selectedCurrencyOfView && !!currentColumnSettings.selectedCurrencyOfView;

      if (isNewCurrencySelection) {
        const flatMetrics = flattenSelectedFamilies(selectedMetrics);
        const updatedMetrics = updateSelectionsByClassifications(flatMetrics, complimentaryFieldsMap, currentColumnSettings);
        state.selectedMetrics = buildFlatSelectedFamilies(updatedMetrics);

        const requiredDimensions = requiredFields
          .filter(field => updatedMetrics.includes(field.id))
          .flatMap(field => field.fields)
          .filter(field => !field.startsWith('metric.'));

        if (requiredDimensions.length > 0) {
          const flatDimensions = flattenSelectedFamilies(selectedDimensions);
          const combinedDimensions = [...new Set([...flatDimensions, ...requiredDimensions])];
          state.selectedDimensions = buildSelectedFamiliesByMap(combinedDimensions, columnAttributeToFamilyIdMap(dimFamilies));
        }
      } else {
        const classifications = buildClassifications(currentColumnSettings);
        state.selectedMetrics = filterSelectedMetricFamilies(selectedMetrics, classifications, complimentaryFieldsMap);
        state.selectedDimensions = filterSelectedDimensionFamilies(selectedDimensions, classifications, complimentaryFieldsMap);
      }

      state.formModificationState.areColumnsModified = true;
    },
    updateSelectedDimensions: (state, action: PayloadAction<{ selectedDimensions: SelectedFamily[] }>) => {
      const { selectedDimensions } = action.payload;
      state.selectedDimensions = selectedDimensions;
      state.formModificationState.areColumnsModified = true;
    },
    updateSelectedMetrics: (state, action: PayloadAction<{ selectedMetrics: SelectedFamily[] }>) => {
      const { selectedMetrics } = action.payload;
      state.selectedMetrics = selectedMetrics;
      state.formModificationState.areColumnsModified = true;
    },
    setColumnSettings: (state, action: PayloadAction<ColumnSettingOptions>) => {
      const previousCurrencyOfView = state.columnSettings.selectedCurrencyOfView;
      const newCurrencyOfView = action.payload.selectedCurrencyOfView;

      state.columnSettings = action.payload;

      if (previousCurrencyOfView !== newCurrencyOfView) {
        state.formModificationState.isCurrencyOfViewModified = true;
      }
    },
    resetFlexibleReportForm: state => {
      state = {
        ...initialState,
        reportName: getDefaultReportName()
      };
      return state;
    },
    resetFormModificationState: state => {
      state.formModificationState = initialFormModificationState;
    },
    clearErrors: state => {
      state.fieldValidationErrors = [];
      state.submitErrors = [];
    },
    setFieldValidationErrors: (state, action: PayloadAction<FieldValidationError[]>) => {
      state.fieldValidationErrors = action.payload;
    },
    setSubmitErrors: (state, action: PayloadAction<ServerValidationError[]>) => {
      state.submitErrors = action.payload;
    },
    copyFlexibleSubscription: (state, action: PayloadAction<FlexibleSubscription>) => {
      const payload = action.payload;
      state.reportConfigurationId = payload.reportConfigurationId;
      state.formModificationState = initialFormModificationState;
      const defaultSchedule =
        payload.reportSchedule.frequency === FREQUENCY.ONCE
          ? getDefaultReportScheduleForFrequency(payload.reportSchedule.frequency as FREQUENCY, payload.reportSchedule?.onceDateOption)
          : getDefaultReportScheduleForFrequency(payload.reportSchedule.frequency as FREQUENCY);

      state.reportSchedule = {
        ...payload.reportSchedule,
        ...(payload.reportSchedule.runDate && isDateInPast(payload.reportSchedule.runDate) && { runDate: defaultSchedule.runDate }),
        ...(payload.reportSchedule.endDate && isDateInPast(payload.reportSchedule.endDate) && { endDate: defaultSchedule.endDate })
      };
      state.reportDelivery = payload.reportDelivery;
      state.reportName = `${payload.reportName.slice(0, DEFAULT_CHARACTER_LIMIT - 5)} Copy`;
      state.reportFormat = payload.reportFormat;
      state.reportFilters = payload.reportFilters;
      state.reportPeriod = payload.reportPeriod;
      state.fieldValidationErrors = [];
      state.submitErrors = [];
      state.columnSettings = {
        selectedCurrencyOfView: payload.columnSettings.selectedCurrencyOfView,
        selectedCurrencyBreakdowns: payload.columnSettings.selectedCurrencyBreakdowns || [],
        isMtaSelected: !!payload.columnSettings.isMtaSelected
      };
    }
  }
});

export const {
  setFilters,
  setManagerAccountsFilter,
  setAdvertisersFilter,
  setReportPeriod,
  setTemplateId,
  setReportName,
  setReportDelivery,
  setReportSchedule,
  setSelectedDimensions,
  removeDimensionFamily,
  removeAllDimensions,
  setSelectedMetrics,
  removeMetricFamily,
  removeAllMetrics,
  setColumnSettings,
  setFormSettingsFromSubscription,
  updateReportDelivery,
  updateReportName,
  updateReportSchedule,
  updateSelectedFamilies,
  updateSelectedDimensions,
  updateSelectedMetrics,
  resetFlexibleReportForm,
  resetFormModificationState,
  clearErrors,
  setFieldValidationErrors,
  setSubmitErrors,
  copyFlexibleSubscription,
  setCampaignIdsFilter,
  setUseAllCampaigns,
  setCampaignCountryFilter,
  setProductTypeFilter
} = FlexibleReportFormSlice.actions;

export const selectFilters = (state: RootState) => state.flexibleReportForm.reportFilters;
export const selectManagerAccountsFilter = createSelector([selectFilters], filters => filters?.managerAccountIds ?? []);
export const selectAdvertisersFilter = createSelector([selectFilters], filters => filters?.advertiserAccountIds ?? []);
export const selectReportPeriod = (state: RootState) => state.flexibleReportForm.reportPeriod;
export const selectReportName = (state: RootState) => state.flexibleReportForm.reportName;
export const selectReportDelivery = (state: RootState) => state.flexibleReportForm.reportDelivery;
export const selectReportSchedule = (state: RootState) => state.flexibleReportForm.reportSchedule;
export const selectReportFormat = (state: RootState) => state.flexibleReportForm.reportFormat;
export const selectSelectedDimensions = (state: RootState) => state.flexibleReportForm.selectedDimensions;
export const selectSelectedMetrics = (state: RootState) => state.flexibleReportForm.selectedMetrics;
export const selectColumnSettings = (state: RootState) => state.flexibleReportForm.columnSettings;
export const getCreateReportFormData = (state: RootState) => state.flexibleReportForm;

export const selectFieldValidationErrors = (state: RootState) => state.flexibleReportForm.fieldValidationErrors;
export const selectSubmitErrors = (state: RootState) => state.flexibleReportForm.submitErrors;
export const selectFormModificationState = (state: RootState) => state.flexibleReportForm.formModificationState;

export const selectCampaignIdsFilter = (state: RootState): string[] => state.flexibleReportForm.reportFilters.campaignIds ?? [];
export const selectUseAllCampaigns = (state: RootState): boolean => state.flexibleReportForm.reportFilters.useAllCampaigns ?? true;
export const selectCampaignCountryFilter = (state: RootState): string[] => state.flexibleReportForm.reportFilters.countries ?? [];
export const selectProductTypeFilter = (state: RootState): string[] => state.flexibleReportForm.reportFilters.productTypes ?? [];

export default FlexibleReportFormSlice.reducer;
